import { useState, useRef } from 'react';
import { Loader2, Download, Upload, CheckCircle2, AlertTriangle, Info } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { toast } from 'sonner';
import api from '../../lib/api';

const KINDS = [
  { kind: 'skills', title: '1 · Skills', desc: 'Skill catalog — family, name, technical/durable. Import this first.' },
  { kind: 'proficiency-levels', title: '2 · Proficiency levels', desc: 'The 0–N scale labels (e.g. Awareness, Proficient, Expert).' },
  { kind: 'skill-themes', title: '3 · Skill themes', desc: 'What "good" looks like at each level per skill. Needs skills.' },
  { kind: 'roles', title: '4 · Job roles & required skills', desc: 'Roles + the target proficiency per required skill. Needs skills.' },
  { kind: 'members', title: '5 · Members & skills', desc: 'People, department, cohort(s) and current skill levels. Needs skills.' },
  { kind: 'member-roles', title: '6 · Role assignments', desc: 'Which member holds which role. Needs members + roles.' },
];

function ImporterCard({ item, orgParam, onImported }) {
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const fileRef = useRef(null);

  const downloadTemplate = async () => {
    try {
      const res = await api.get(`/capability-inventory/import/${item.kind}/template${orgParam}`, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `capability_${item.kind}_template.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error('Could not download template');
    }
  };

  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    setResult(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await api.post(`/capability-inventory/import/${item.kind}${orgParam}`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setResult(res.data);
      const { inserted, updated, skipped, errors } = res.data;
      if (errors?.length) {
        toast.warning(`Imported with ${errors.length} row error(s): +${inserted} / ~${updated} / skipped ${skipped}`);
      } else {
        toast.success(`${item.title.split('·')[1].trim()}: +${inserted} added, ${updated} updated`);
      }
      onImported?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Import failed');
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  return (
    <Card className="border-slate-200" data-testid={`cap-import-card-${item.kind}`}>
      <CardHeader className="pb-3">
        <CardTitle className="text-sm text-slate-800">{item.title}</CardTitle>
        <CardDescription className="text-xs">{item.desc}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-2">
        <div className="flex items-center gap-2">
          <Button size="sm" variant="outline" className="h-8 text-xs gap-1.5" onClick={downloadTemplate}
            data-testid={`cap-import-template-${item.kind}`}>
            <Download className="h-3.5 w-3.5" /> Template
          </Button>
          <input ref={fileRef} type="file" accept=".csv" className="hidden"
            onChange={(e) => upload(e.target.files?.[0])}
            data-testid={`cap-import-file-${item.kind}`} />
          <Button size="sm" className="h-8 text-xs gap-1.5 bg-teal-600 hover:bg-teal-700" disabled={busy}
            onClick={() => fileRef.current?.click()} data-testid={`cap-import-upload-${item.kind}`}>
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />}
            Upload CSV
          </Button>
        </div>
        {result && (
          <div className="text-xs rounded-md border border-slate-100 bg-slate-50 p-2 space-y-1" data-testid={`cap-import-result-${item.kind}`}>
            <p className="flex items-center gap-1.5 text-slate-700">
              <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" />
              {result.rows_read} rows · <strong>+{result.inserted}</strong> added · {result.updated} updated · {result.skipped} skipped
            </p>
            {result.warnings?.slice(0, 5).map((w, i) => (
              <p key={i} className="flex items-start gap-1.5 text-amber-700"><Info className="h-3 w-3 mt-0.5 shrink-0" />{w}</p>
            ))}
            {result.errors?.slice(0, 8).map((er, i) => (
              <p key={i} className="flex items-start gap-1.5 text-rose-700"><AlertTriangle className="h-3 w-3 mt-0.5 shrink-0" />{er}</p>
            ))}
            {result.errors?.length > 8 && <p className="text-rose-600">…and {result.errors.length - 8} more errors</p>}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default function CapabilityDataImporter({ selectedOrg = '' }) {
  const orgParam = selectedOrg ? `?organization_id=${selectedOrg}` : '';
  return (
    <div className="space-y-4" data-testid="cap-data-importer">
      <Card className="border-teal-200 bg-gradient-to-br from-teal-50/50 to-white">
        <CardHeader>
          <CardTitle className="text-teal-800 text-base">Capability data import</CardTitle>
          <CardDescription>
            Replace the demo data with your own capability inventory. Import in order (1 → 6) —
            later files reference skills, roles and members by name, so earlier steps must exist first.
            Re-uploading updates existing records. Cohort names must match your existing cohorts.
          </CardDescription>
        </CardHeader>
      </Card>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {KINDS.map(item => (
          <ImporterCard key={item.kind} item={item} orgParam={orgParam} />
        ))}
      </div>
    </div>
  );
}
